import React from 'react';
import {
  TextField, Typography, Box, Grid, Button, Paper, Snackbar, Alert, Chip,
} from '@mui/material';
import IconPiesJuntos from '../../../images/pies_juntos.png';
import IconSemitandem from '../../../images/semitandem.png';
import IconTandem from '../../../images/tandem.png';
import { useCapacidadFuncionalForm } from '../hooks/useCapacidadFuncionalForm';

const FORM_COLORS = {
  primary: '#0d6aee',
  textMain: '#0f172a',
  textMuted: '#475569',
  border: '#dbe7f3',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    '& fieldset': { borderColor: '#cbd5e1' },
    '&:hover fieldset': { borderColor: FORM_COLORS.primary },
    '&.Mui-focused fieldset': { borderColor: FORM_COLORS.primary, borderWidth: 2 },
  },
};

const sectionPaperSx = {
  p: { xs: 2, md: 3 },
  mb: 3,
  borderRadius: 3,
  border: `1px solid ${FORM_COLORS.border}`,
  background: '#f8fbff',
  boxShadow: '0 4px 12px rgba(15,23,42,0.04)',
};

const CapacidadFuncional = ({ idPaciente }) => {
  const {
    formData,
    velocidadMarcha,
    sppbTotal,
    esFragilTUG,
    openGood,
    openError,
    setOpenGood,
    setOpenError,
    handleChange,
    guardarDatos,
  } = useCapacidadFuncionalForm({ idPaciente });

  const esFragil = sppbTotal < 10 || esFragilTUG;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <Paper
        elevation={0}
        sx={{ width: '100%', p: { xs: 2, md: 3 }, borderRadius: 4, border: `1px solid ${FORM_COLORS.border}` }}
      >
        <Typography variant="h4" sx={{ textAlign: 'center', color: FORM_COLORS.textMain, fontWeight: 700, mb: 1 }}>
          Capacidad Funcional / SPPB – TUG
        </Typography>
        <Typography sx={{ textAlign: 'center', color: FORM_COLORS.textMuted, mb: 4 }}>
          Evaluación del equilibrio, velocidad de marcha y movilidad funcional.
        </Typography>

        <TextField
          label="ID del paciente"
          variant="outlined"
          value={idPaciente}
          disabled
          sx={{ mb: 4, ...fieldSx }}
        />

        {/* Equilibrio */}
        <Paper elevation={0} sx={sectionPaperSx}>
          <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700, mb: 2 }}>
            Prueba de equilibrio estático
          </Typography>
          {[
            { icon: IconPiesJuntos, alt: 'Pies juntos', label: 'Tiempo pies juntos (s)', name: 'tiempoPiesJuntos' },
            { icon: IconSemitandem, alt: 'Semitándem', label: 'Tiempo semitándem (s)', name: 'tiempoSemitandem' },
            { icon: IconTandem, alt: 'Tándem', label: 'Tiempo tándem (s)', name: 'tiempoTandem' },
          ].map(({ icon, alt, label, name }) => (
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }} key={name}>
              <Grid item xs={3} sm={2}>
                <img src={icon} alt={alt} height={72} style={{ display: 'block' }} />
              </Grid>
              <Grid item xs={9} sm={10}>
                <TextField
                  fullWidth
                  label={label}
                  variant="outlined"
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
          ))}
        </Paper>

        {/* Desplazamiento */}
        <Paper elevation={0} sx={sectionPaperSx}>
          <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700, mb: 0.5 }}>
            Prueba de desplazamiento 4 m
          </Typography>
          <Typography variant="body2" sx={{ color: FORM_COLORS.textMuted, mb: 2 }}>
            Dos tomas de tiempo, se toma el menor. Primera puntera, último talón.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tiempo (s)"
                variant="outlined"
                name="tiempoDesplazamiento"
                value={formData.tiempoDesplazamiento}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Velocidad de marcha (m/s)"
                variant="outlined"
                value={velocidadMarcha}
                disabled
                sx={fieldSx}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Fuerza */}
        <Paper elevation={0} sx={sectionPaperSx}>
          <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700, mb: 0.5 }}>
            5 sentadillas
          </Typography>
          <Typography variant="body2" sx={{ color: FORM_COLORS.textMuted, mb: 2 }}>
            Brazos cruzados sobre el pecho, inicio sentado, finaliza en 5ª bipedestación.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tiempo (s)"
                variant="outlined"
                name="tiempoSentadillas"
                value={formData.tiempoSentadillas}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* TUG */}
        <Paper elevation={0} sx={sectionPaperSx}>
          <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700, mb: 0.5 }}>
            Timed Up &amp; Go (TUG)
          </Typography>
          <Typography variant="body2" sx={{ color: FORM_COLORS.textMuted, mb: 2 }}>
            Levantarse de la silla con apoyo de brazos, caminar 3 m y volver a sentarse.
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tiempo (s)"
                variant="outlined"
                name="tiempoTUG"
                value={formData.tiempoTUG}
                onChange={handleChange}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {formData.tiempoTUG && (
                <Chip
                  label={esFragilTUG ? 'Frágil (≥ 12 s)' : 'Normal (< 12 s)'}
                  color={esFragilTUG ? 'error' : 'success'}
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Resultados */}
        <Paper elevation={0} sx={{ ...sectionPaperSx, background: '#ffffff' }}>
          <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700, mb: 2 }}>
            Resultados
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>PUNTUACIÓN SPPB</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: FORM_COLORS.textMain, lineHeight: 1.2 }}>
                {sppbTotal}
              </Typography>
              <Typography variant="body2" sx={{ color: FORM_COLORS.textMuted }}>puntos</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>VELOCIDAD MARCHA</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: FORM_COLORS.textMain, lineHeight: 1.2 }}>
                {velocidadMarcha || '—'}
              </Typography>
              <Typography variant="body2" sx={{ color: FORM_COLORS.textMuted }}>m/s</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>TUG</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: FORM_COLORS.textMain, lineHeight: 1.2 }}>
                {formData.tiempoTUG || '—'}
              </Typography>
              <Typography variant="body2" sx={{ color: FORM_COLORS.textMuted }}>s</Typography>
            </Grid>
          </Grid>

          <Box sx={{ mt: 1 }}>
            <Chip
              label={
                !esFragil
                  ? 'No frágil — SPPB ≥ 10 y TUG < 12 s'
                  : 'Frágil — SPPB < 10 o TUG ≥ 12 s'
              }
              color={!esFragil ? 'success' : 'error'}
              sx={{ fontWeight: 600 }}
            />
            {formData.tiempoSentadillas && parseFloat(formData.tiempoSentadillas) >= 11.64 && (
              <Chip
                label="Pérdida de fuerza muscular (≥ 11.64 s)"
                color="warning"
                sx={{ fontWeight: 600, ml: 1 }}
              />
            )}
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="contained" color="primary" onClick={guardarDatos} sx={{ borderRadius: 2.5, px: 4 }}>
            Guardar datos
          </Button>
        </Box>

        <Snackbar open={openGood} autoHideDuration={2000} onClose={() => setOpenGood(false)}>
          <Alert severity="success" onClose={() => setOpenGood(false)}>
            ¡Datos guardados correctamente!
          </Alert>
        </Snackbar>
        <Snackbar open={openError} autoHideDuration={4000} onClose={() => setOpenError(false)}>
          <Alert severity="error" onClose={() => setOpenError(false)}>
            Error al guardar los datos.
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default CapacidadFuncional;
